import { useState, useMemo, useEffect, useImperativeHandle, forwardRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Add, Trash, InfoCircle } from "iconsax-react";
import { Button, Input, Select, DatePicker, Dropdown, RadioButton, RadioGroup } from "@/shared/ui";
import { useLocale } from "@/shared/hooks";
import { extractErrorMessage } from "@/shared/utils";
import { useCreateInKindDonationMutation } from "@/features/accounting/smeta";
import { formatMoneyKzt, formatPercentageFromRate } from "@/features/accounting/shared";
import type { AssetType, FixedAssetGroup, InventorySubtype, InKindUnit } from "@/features/accounting/smeta";
import { FIXED_ASSET_GROUP_DEFAULTS, isFixedRateGroup } from "@/features/accounting/warehouse";
import type { PromptState } from "../../../hooks/useIncomeModals";

const ASSET_TYPES: AssetType[] = ["fixed_asset", "inventory", "intangible"];
const FIXED_ASSET_GROUPS: FixedAssetGroup[] = ["group1", "group2", "group3", "group4", "other"];
const INVENTORY_SUBTYPES: InventorySubtype[] = ["consumable", "reusable"];
const UNITS: InKindUnit[] = ["pcs", "kg", "m", "m2", "m3", "l"];

function createInKindSchema() {
  return z.object({
    donor_name: z.string().min(1, "inKind.validation.donorNameRequired"),
    donor_bin: z.string().optional(),
    document_number: z.string().min(1, "inKind.validation.documentNumberRequired"),
    document_date: z.date({ error: "inKind.validation.documentDateRequired" }),
    description: z.string().optional(),
    pool: z.enum(["internal", "external"], { message: "inKind.validation.poolRequired" }),
    items: z
      .array(
        z.object({
          name: z.string().min(1, "inKind.validation.itemNameRequired"),
          asset_type: z.enum(["fixed_asset", "inventory", "intangible"], {
            message: "inKind.validation.assetTypeRequired",
          }),
          quantity: z
            .number({ error: "inKind.validation.quantityRequired" })
            .min(1, "inKind.validation.quantityMin"),
          unit_price: z.string().min(1, "inKind.validation.unitPriceRequired"),
          unit: z.enum(["pcs", "kg", "m", "m2", "m3", "l"]).nullable().optional(),
          fixed_asset_group: z.string().nullable().optional(),
          inventory_subtype: z.string().nullable().optional(),
          useful_life_months: z.number().nullable().optional(),
          depreciation_rate: z.string().nullable().optional(),
        }),
      )
      .min(1, "inKind.validation.atLeastOneItem")
      .superRefine(function validateItems(items, ctx) {
        items.forEach(function validateItem(item, index) {
          if (Number(item.unit_price) <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "inKind.validation.unitPriceMin",
              path: [index, "unit_price"],
            });
          }
          if (item.asset_type === "fixed_asset") {
            if (!item.fixed_asset_group) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "inKind.validation.fixedAssetGroupRequired",
                path: [index, "fixed_asset_group"],
              });
            }
            if (!item.useful_life_months || item.useful_life_months <= 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "inKind.validation.usefulLifeRequired",
                path: [index, "useful_life_months"],
              });
            }
            if (!item.depreciation_rate || Number(item.depreciation_rate) < 0 || Number(item.depreciation_rate) > 100) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "inKind.validation.depreciationRateRequired",
                path: [index, "depreciation_rate"],
              });
            }
          }
          if (item.asset_type === "inventory") {
            if (!item.inventory_subtype) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "inKind.validation.inventorySubtypeRequired",
                path: [index, "inventory_subtype"],
              });
            }
          }
          if (item.asset_type === "intangible") {
            if (!item.useful_life_months || item.useful_life_months <= 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "inKind.validation.usefulLifeRequired",
                path: [index, "useful_life_months"],
              });
            }
            if (!item.depreciation_rate || Number(item.depreciation_rate) < 0 || Number(item.depreciation_rate) > 100) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "inKind.validation.depreciationRateRequired",
                path: [index, "depreciation_rate"],
              });
            }
          }
        });
      }),
  });
}

type FormValues = z.infer<ReturnType<typeof createInKindSchema>>;

export interface InKindDonationFormHandle {
  submit: () => void;
  reset: () => void;
}

interface Props {
  disabled?: boolean;
  setPrompt: (state: PromptState | null) => void;
  onSuccess: () => void;
  onSubmittingChange: (submitting: boolean) => void;
}

const InKindDonationForm = forwardRef<InKindDonationFormHandle, Props>(function InKindDonationForm(
  { disabled = false, setPrompt, onSuccess, onSubmittingChange },
  ref,
) {
  const { t } = useTranslation("IncomePage");
  const locale = useLocale();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createInKindDonation] = useCreateInKindDonationMutation();
  const [amortizationModes, setAmortizationModes] = useState<Record<number, "percentage" | "months">>({});
  const [hoveredTooltipIndex, setHoveredTooltipIndex] = useState<number | null>(null);

  const schema = useMemo(createInKindSchema, []);
  const {
    register,
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      donor_name: "",
      donor_bin: "",
      document_number: "",
      description: "",
      pool: "internal",
      items: [
        {
          name: "",
          asset_type: "inventory" as AssetType,
          quantity: 1,
          unit_price: "",
          unit: "pcs" as InKindUnit,
          fixed_asset_group: null,
          inventory_subtype: null,
          useful_life_months: null,
          depreciation_rate: null,
        },
      ],
    },
  });

  useImperativeHandle(
    ref,
    function exposeHandle() {
      return {
        submit: handleSubmit(onSubmit),
        reset: function resetAll() {
          reset();
          setAmortizationModes({});
          setHoveredTooltipIndex(null);
        },
      };
    },
    [handleSubmit, reset],
  );

  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const watchedItems = watch("items");

  const assetTypesKey = watchedItems.map((i) => i.asset_type).join(",");

  useEffect(
    function clearStaleFieldsOnAssetTypeChange() {
      watchedItems.forEach(function checkItem(item, index) {
        if (item.asset_type !== "fixed_asset") {
          if (item.fixed_asset_group) setValue(`items.${index}.fixed_asset_group`, null);
        }
        if (item.asset_type !== "inventory") {
          if (item.inventory_subtype) setValue(`items.${index}.inventory_subtype`, null);
        }
        if (item.asset_type !== "fixed_asset" && item.asset_type !== "intangible") {
          if (item.useful_life_months) setValue(`items.${index}.useful_life_months`, null);
          if (item.depreciation_rate) setValue(`items.${index}.depreciation_rate`, null);
        }
      });
    },
    [assetTypesKey],
  );

  const pool = watch("pool");

  const poolOptions = useMemo(
    function buildPoolOptions() {
      return [
        { value: "internal", label: t("modals.create.poolInternal") },
        { value: "external", label: t("modals.create.poolExternal") },
      ];
    },
    [t],
  );

  const assetTypeOptions = ASSET_TYPES.map(function buildAssetTypeOption(type) {
    return { label: t(`inKind.assetTypes.${type}`), value: type };
  });

  const fixedAssetGroupOptions = FIXED_ASSET_GROUPS.map(function buildFAGOption(group) {
    return { label: t(`inKind.fixedAssetGroups.${group}`), value: group };
  });

  const inventorySubtypeOptions = INVENTORY_SUBTYPES.map(function buildInvOption(subtype) {
    return { label: t(`inKind.inventorySubtypes.${subtype}`), value: subtype };
  });

  const unitOptions = UNITS.map(function buildUnitOption(unit) {
    return { label: t(`inKind.units.${unit}`), value: unit };
  });

  const amortizationModeOptions = useMemo(
    function buildAmortModeOptions() {
      return [
        { label: t("inKind.amortizationModePercentage"), value: "percentage" as const },
        { label: t("inKind.amortizationModeMonths"), value: "months" as const },
      ];
    },
    [t],
  );

  const total = useMemo(
    function computeTotal() {
      return watchedItems.reduce(function sumItem(acc, item) {
        return acc + (item.quantity || 0) * (Number(item.unit_price) || 0);
      }, 0);
    },
    [watchedItems],
  );

  function handleAmortizationValueChange(index: number, mode: "percentage" | "months", rawValue: string) {
    if (mode === "percentage") {
      const pct = Number(rawValue);
      if (!isNaN(pct) && pct > 0 && pct <= 100) {
        const rate = pct / 100;
        const months = Math.round(12 / rate);
        setValue(`items.${index}.depreciation_rate`, rawValue, { shouldValidate: true });
        setValue(`items.${index}.useful_life_months`, months, { shouldValidate: true });
      } else {
        setValue(`items.${index}.depreciation_rate`, rawValue || null, { shouldValidate: true });
        setValue(`items.${index}.useful_life_months`, null);
      }
    } else {
      const months = Number(rawValue);
      if (!isNaN(months) && months > 0) {
        const rate = 12 / months;
        setValue(`items.${index}.useful_life_months`, months, { shouldValidate: true });
        setValue(`items.${index}.depreciation_rate`, String(Math.round(rate * 100)), { shouldValidate: true });
      } else {
        setValue(`items.${index}.useful_life_months`, rawValue ? Number(rawValue) : null, { shouldValidate: true });
        setValue(`items.${index}.depreciation_rate`, null);
      }
    }
  }

  function getDerived(index: number, mode: "percentage" | "months") {
    const item = watchedItems[index];
    if (!item) return null;
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unit_price) || 0;
    if (!qty || !price) return null;
    const totalValue = qty * price;

    if (mode === "percentage") {
      const rate = Number(item.depreciation_rate) || 0;
      if (rate > 0 && rate <= 100) {
        const decRate = rate / 100;
        const months = Math.round(12 / decRate);
        const monthly = (totalValue * decRate) / 12;
        return { months, monthlyAmount: monthly };
      }
    } else {
      const months = item.useful_life_months || 0;
      if (months > 0) {
        const rate = 12 / months;
        const monthly = (totalValue * rate) / 12;
        return { rate, monthlyAmount: monthly };
      }
    }
    return null;
  }

  function renderAmortizationTooltip(index: number) {
    const tooltipIndex = index * 10 + 1;
    return (
      <div
        className="relative flex items-center"
        onMouseEnter={() => setHoveredTooltipIndex(tooltipIndex)}
        onMouseLeave={() => setHoveredTooltipIndex(null)}>
        <Dropdown
          open={hoveredTooltipIndex === tooltipIndex}
          onClose={() => setHoveredTooltipIndex(null)}
          direction="bottom"
          align="right"
          width="w-max"
          className="elevation-level-2!">
          <InfoCircle
            size={14}
            color="currentColor"
            className="content-action-neutral cursor-pointer hover:opacity-80"
          />
          <div
            className="p-3 flex flex-col gap-1 w-48"
            onMouseEnter={() => setHoveredTooltipIndex(tooltipIndex)}
            onMouseLeave={() => setHoveredTooltipIndex(null)}>
            <p className="text-label-xs content-base-primary">{t("inKind.depreciationAutoCalcTooltip")}</p>
          </div>
        </Dropdown>
      </div>
    );
  }

  function renderAmortizationValueInput(index: number) {
    const mode = amortizationModes[index] ?? "months";
    const inputDisabled = isDisabled || isFixedRateGroup(watchedItems[index]?.fixed_asset_group ?? null);
    if (mode === "percentage") {
      return (
        <Input
          placeholder={t("inKind.amortizationRatePlaceholder")}
          value={watchedItems[index]?.depreciation_rate ?? ""}
          onChange={(e) => handleAmortizationValueChange(index, "percentage", e.target.value)}
          disabled={inputDisabled}
          onlyNumber
        />
      );
    }
    return (
      <Input
        placeholder={t("inKind.amortizationMonthsPlaceholder")}
        value={watchedItems[index]?.useful_life_months?.toString() ?? ""}
        onChange={(e) => handleAmortizationValueChange(index, "months", e.target.value)}
        disabled={inputDisabled}
        onlyNumber
      />
    );
  }

  function renderDerivedText(index: number) {
    const mode = amortizationModes[index] ?? "months";
    const derived = getDerived(index, mode);
    if (!derived) return null;

    if (mode === "percentage" && derived.months) {
      return (
        <p className="text-label-xs content-action-neutral">
          {derived.months} {t("inKind.months")} = {formatMoneyKzt(derived.monthlyAmount || 0)} / {t("inKind.month")}
        </p>
      );
    }
    if (mode === "months" && derived.rate) {
      return (
        <p className="text-label-xs content-action-neutral">
          {formatPercentageFromRate(derived.rate)} = {formatMoneyKzt(derived.monthlyAmount || 0)} / {t("inKind.month")}
        </p>
      );
    }
    return null;
  }

  async function onSubmit(data: FormValues) {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      onSubmittingChange(true);
      const body = {
        donor_name: data.donor_name,
        ...(data.donor_bin && { donor_bin: data.donor_bin }),
        document_number: data.document_number,
        document_date: data.document_date.toISOString().split("T")[0],
        ...(data.description && { description: data.description }),
        pool: data.pool,
        items: data.items.map(function mapItem(item) {
          const mapped: Record<string, unknown> = {
            name: item.name,
            asset_type: item.asset_type,
            quantity: item.quantity,
            unit_price: item.unit_price,
          };
          if (item.unit) mapped.unit = item.unit;
          if (item.asset_type === "fixed_asset") {
            mapped.fixed_asset_group = item.fixed_asset_group;
            mapped.useful_life_months = item.useful_life_months;
            mapped.depreciation_rate = String(Number(item.depreciation_rate) / 100);
          }
          if (item.asset_type === "inventory") {
            mapped.inventory_subtype = item.inventory_subtype;
          }
          if (item.asset_type === "intangible") {
            mapped.useful_life_months = item.useful_life_months;
            mapped.depreciation_rate = String(Number(item.depreciation_rate) / 100);
          }
          return mapped;
        }),
      };
      await createInKindDonation(body as any).unwrap();
      setPrompt({
        title: t("messages.inKindSuccessTitle"),
        text: t("messages.inKindSuccessText"),
        variant: "success",
      });
      reset();
      setAmortizationModes({});
      onSuccess();
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.inKindErrorText", t),
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
      onSubmittingChange(false);
    }
  }

  function handleAddItem() {
    append({
      name: "",
      asset_type: "inventory" as AssetType,
      quantity: 1,
      unit_price: "",
      unit: "pcs" as InKindUnit,
      fixed_asset_group: null,
      inventory_subtype: null,
      useful_life_months: null,
      depreciation_rate: null,
    });
  }

  function handleRemoveItem(index: number) {
    remove(index);
    setHoveredTooltipIndex(null);
    setAmortizationModes(function remap(prev) {
      const next: Record<number, "percentage" | "months"> = {};
      for (const [k, v] of Object.entries(prev)) {
        const key = Number(k);
        if (key < index) next[key] = v;
        else if (key > index) next[key - 1] = v;
      }
      return next;
    });
  }

  function handleFixedAssetGroupChangeInKind(index: number, value: string | null) {
    setValue(`items.${index}.fixed_asset_group`, value, { shouldValidate: true });
    if (isFixedRateGroup(value)) {
      const defaults = FIXED_ASSET_GROUP_DEFAULTS[value as Exclude<FixedAssetGroup, "other">];
      setValue(`items.${index}.depreciation_rate`, String(Math.round(defaults.rate * 100)), { shouldValidate: true });
      setValue(`items.${index}.useful_life_months`, defaults.months, { shouldValidate: true });
    } else {
      setValue(`items.${index}.depreciation_rate`, null);
      setValue(`items.${index}.useful_life_months`, null);
    }
  }

  const isDisabled = disabled || isSubmitting;

  return (
    <div className="flex flex-col gap-4 py-5">
      <div className="surface-container-fill radius-xl p-4 flex flex-col gap-4">
        <p className="text-body-bold-md content-base-primary">{t("inKind.donorBlock")}</p>
        <Input
          label={t("inKind.donorName")}
          {...register("donor_name")}
          placeholder={t("inKind.donorNamePlaceholder")}
          error={errors.donor_name?.message ? t(errors.donor_name.message) : undefined}
          disabled={isDisabled}
          hasRequiredLabel
        />
        <Input
          label={t("inKind.donorBin")}
          {...register("donor_bin")}
          placeholder={t("inKind.donorBinPlaceholder")}
          maxLength={12}
          disabled={isDisabled}
        />
      </div>

      <div className="surface-container-fill radius-xl p-4 flex flex-col gap-4">
        <p className="text-body-bold-md content-base-primary">{t("inKind.documentBlock")}</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label={t("inKind.documentNumber")}
            {...register("document_number")}
            placeholder={t("inKind.documentNumberPlaceholder")}
            error={errors.document_number?.message ? t(errors.document_number.message) : undefined}
            disabled={isDisabled}
            hasRequiredLabel
          />
          <DatePicker
            label={t("inKind.documentDate")}
            value={watch("document_date") ?? null}
            onChange={(date) => setValue("document_date", date as Date, { shouldValidate: true })}
            mode="single"
            locale={locale}
            error={errors.document_date?.message ? t(errors.document_date.message) : undefined}
            disabled={isDisabled}
            hasRequiredLabel
          />
        </div>
        <Input
          label={t("inKind.description")}
          {...register("description")}
          placeholder={t("inKind.descriptionPlaceholder")}
          isTextarea
          disabled={isDisabled}
        />
      </div>

      <div className="surface-container-fill radius-xl p-4 flex flex-col gap-4">
        <p className="text-body-bold-md content-base-primary">{t("modals.create.poolBlock")}</p>
        <RadioGroup
          name="in_kind_pool"
          value={pool}
          onChange={(val) => setValue("pool", val as "internal" | "external", { shouldValidate: true })}
          wrapperClassName="flex flex-col gap-2">
          {poolOptions.map(function renderPoolOption(option) {
            return <RadioButton key={option.value} value={option.value} label={option.label} />;
          })}
        </RadioGroup>
      </div>

      <div className="surface-container-fill radius-xl p-4 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <p className="text-body-bold-md content-base-primary">{t("inKind.itemsBlock")}</p>
          <Button variant="secondary" size="sm" onClick={handleAddItem} disabled={isDisabled}>
            <Add size={16} color="currentColor" />
            {t("inKind.addItem")}
          </Button>
        </div>

        {errors.items?.root?.message && (
          <p className="text-body-regular-sm content-action-negative">{t(errors.items.root.message)}</p>
        )}

        {fields.map(function renderItemRow(field, index) {
          const itemType = watchedItems[index]?.asset_type;
          const showFixedAssetFields = itemType === "fixed_asset";
          const showInventoryFields = itemType === "inventory";
          const showIntangibleFields = itemType === "intangible";

          return (
            <div key={field.id} className="surface-secondary-fill radius-lg p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-body-bold-sm content-base-primary">#{index + 1}</span>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    className="text-body-regular-sm content-action-negative cursor-pointer flex items-center gap-1"
                    onClick={() => handleRemoveItem(index)}
                    disabled={isDisabled}>
                    <Trash size={14} color="currentColor" />
                    {t("inKind.removeItem")}
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={t("inKind.itemName")}
                  {...register(`items.${index}.name`)}
                  placeholder={t("inKind.itemNamePlaceholder")}
                  error={errors.items?.[index]?.name?.message ? t(errors.items[index].name.message) : undefined}
                  disabled={isDisabled}
                  hasRequiredLabel
                />
                <Select<string>
                  label={t("inKind.assetType")}
                  options={assetTypeOptions}
                  value={watchedItems[index]?.asset_type ?? null}
                  onChange={(val) => setValue(`items.${index}.asset_type`, val as AssetType, { shouldValidate: true })}
                  placeholder={t("inKind.assetTypePlaceholder")}
                  disabled={isDisabled}
                  hasRequiredLabel
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Input
                  label={t("inKind.quantity")}
                  value={watchedItems[index]?.quantity?.toString() ?? ""}
                  onChange={(e) =>
                    setValue(`items.${index}.quantity`, Number(e.target.value) || 0, { shouldValidate: true })
                  }
                  placeholder={t("inKind.quantityPlaceholder")}
                  onlyNumber
                  error={errors.items?.[index]?.quantity?.message ? t(errors.items[index].quantity.message) : undefined}
                  disabled={isDisabled}
                  hasRequiredLabel
                />
                <Select<string>
                  label={t("inKind.unit")}
                  options={unitOptions}
                  value={watchedItems[index]?.unit ?? null}
                  onChange={(val) =>
                    setValue(`items.${index}.unit`, val as InKindUnit | null, { shouldValidate: true })
                  }
                  placeholder={t("inKind.unitPlaceholder")}
                  disabled={isDisabled}
                />
                <Input
                  label={t("inKind.unitPrice")}
                  value={watchedItems[index]?.unit_price ?? ""}
                  onChange={(e) => setValue(`items.${index}.unit_price`, e.target.value, { shouldValidate: true })}
                  placeholder={t("inKind.unitPricePlaceholder")}
                  onlyNumber
                  error={
                    errors.items?.[index]?.unit_price?.message ? t(errors.items[index].unit_price.message) : undefined
                  }
                  disabled={isDisabled}
                  hasRequiredLabel
                />
              </div>

              {showInventoryFields && (
                <Select<string>
                  label={t("inKind.inventorySubtype")}
                  options={inventorySubtypeOptions}
                  value={(watchedItems[index]?.inventory_subtype as string) ?? null}
                  onChange={(val) => setValue(`items.${index}.inventory_subtype`, val, { shouldValidate: true })}
                  placeholder={t("inKind.inventorySubtypePlaceholder")}
                  error={
                    errors.items?.[index]?.inventory_subtype?.message
                      ? t(errors.items[index].inventory_subtype.message)
                      : undefined
                  }
                  disabled={isDisabled}
                  hasRequiredLabel
                />
              )}

              {showFixedAssetFields && (
                <Select<string>
                  label={t("inKind.fixedAssetGroup")}
                  options={fixedAssetGroupOptions}
                  value={(watchedItems[index]?.fixed_asset_group as string) ?? null}
                  onChange={(val) => handleFixedAssetGroupChangeInKind(index, val)}
                  placeholder={t("inKind.fixedAssetGroupPlaceholder")}
                  error={
                    errors.items?.[index]?.fixed_asset_group?.message
                      ? t(errors.items[index].fixed_asset_group.message)
                      : undefined
                  }
                  disabled={isDisabled}
                  hasRequiredLabel
                />
              )}

              {showFixedAssetFields && (
                <div className="flex flex-col gap-2 mt-auto">
                  <div className="flex items-center gap-2">
                    <label className="text-label-sm content-base-primary">
                      {t("inKind.amortization")}
                      <span className="content-action-negative ml-1">*</span>
                    </label>
                    {renderAmortizationTooltip(index)}
                  </div>
                  <div className="grid grid-cols-[266fr_200fr] gap-2">
                    <Select<string>
                      options={amortizationModeOptions}
                      value={amortizationModes[index] ?? "months"}
                      onChange={(val) =>
                        val && setAmortizationModes((prev) => ({ ...prev, [index]: val as "percentage" | "months" }))
                      }
                      disabled={isDisabled}
                    />
                    {renderAmortizationValueInput(index)}
                  </div>
                  {renderDerivedText(index)}
                </div>
              )}

              {showIntangibleFields && (
                <div className="flex flex-col gap-2 mt-auto">
                  <div className="flex items-center gap-2">
                    <label className="text-label-sm content-base-primary">
                      {t("inKind.amortization")}
                      <span className="content-action-negative ml-1">*</span>
                    </label>
                    {renderAmortizationTooltip(index)}
                  </div>
                  <div className="grid grid-cols-[266fr_200fr] gap-2">
                    <Select<string>
                      options={amortizationModeOptions}
                      value={amortizationModes[index] ?? "months"}
                      onChange={(val) =>
                        val && setAmortizationModes((prev) => ({ ...prev, [index]: val as "percentage" | "months" }))
                      }
                      disabled={isDisabled}
                    />
                    {renderAmortizationValueInput(index)}
                  </div>
                  {renderDerivedText(index)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="surface-container-fill radius-xl p-4 flex items-center justify-between">
        <span className="text-body-bold-md content-base-primary">{t("inKind.total")}</span>
        <span className="text-body-bold-md content-base-primary">
          {total.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₸
        </span>
      </div>
    </div>
  );
});

export default InKindDonationForm;

